import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Loader2, TrendingUp, AlertTriangle, DollarSign, Truck, Package } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function MaterialTrendsPage() {
  const { t } = useLang();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await api.get(`/materials/trends?days=${days}`)); }
    catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const topChart = data.top_materials.slice(0, 10).map((m) => ({
    name: m.name.length > 28 ? m.name.slice(0, 28) + "…" : m.name,
    fullName: m.name,
    spent: m.total_spent,
    count: m.count,
  }));

  const weeklyChart = data.weekly.map((w) => ({ week: w.week.slice(-3), spend: w.spend, count: w.count }));

  return (
    <div className="space-y-6" data-testid="material-trends-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
            <TrendingUp className="w-4 h-4" /> Price intelligence
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mt-1">Material Price Trends</h1>
          <p className="text-sm text-slate-500 mt-1">Data collected from POs and AI-scanned invoices over the last {days} days.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {[30, 90, 180, 365].map((d) => (
            <button
              key={d}
              data-testid={`trends-range-${d}`}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                days === d ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >{d}d</button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI icon={DollarSign} label="Top-20 spend" value={money(data.total_spent_top)} color="emerald" />
        <KPI icon={Package} label="Distinct materials" value={data.top_materials.length} color="blue" />
        <KPI icon={Truck} label="Suppliers active" value={data.suppliers.length} color="indigo" />
        <KPI icon={AlertTriangle} label="Price alerts" value={data.price_alerts.length}
             color={data.price_alerts.length ? "red" : "slate"} />
      </div>

      {/* Weekly spend */}
      <Card title="Weekly spend" subtitle="Total material purchases per ISO week">
        <div className="h-64" data-testid="trends-weekly">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <LineChart data={weeklyChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} />
              <Line type="monotone" dataKey="spend" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Top materials + alerts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top 10 materials by spend" subtitle="Where the money is going">
          <div className="h-64" data-testid="trends-top">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={topChart} layout="vertical" margin={{ left: 8, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => money(v)} labelFormatter={(_, p) => p?.[0]?.payload?.fullName} />
                <Bar dataKey="spent" radius={[0, 4, 4, 0]}>
                  {topChart.map((_, i) => <Cell key={i} fill={`hsl(${210 + i * 12}, 65%, 55%)`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title="Price alerts"
          subtitle="Items whose last unit price rose >20% vs average"
          accent={data.price_alerts.length ? "border-red-200 bg-red-50/30" : ""}
        >
          {data.price_alerts.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No price surges detected. 👍</div>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="trends-alerts">
              {data.price_alerts.map((a, i) => (
                <li key={i} className="rounded-lg border border-red-200 bg-white p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-slate-800 truncate flex-1">{a.name}</span>
                    <span className="text-xs font-bold text-red-600 tabular-nums shrink-0">+{a.delta_pct}%</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5 text-[10px] text-slate-500">
                    <span className="truncate">{a.supplier}</span>
                    <span className="tabular-nums shrink-0">
                      avg ${a.avg_price.toFixed(2)} → last ${a.last_price.toFixed(2)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Suppliers ranking */}
      <Card title="Top suppliers" subtitle="Ranked by total spent in the window">
        <div className="overflow-x-auto" data-testid="trends-suppliers">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase font-bold text-slate-400 border-b border-slate-200">
                <th className="text-left py-2 px-2">#</th>
                <th className="text-left py-2 px-2">Supplier</th>
                <th className="text-right py-2 px-2">Orders</th>
                <th className="text-right py-2 px-2">Avg ticket</th>
                <th className="text-right py-2 px-2">Total spent</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {data.suppliers.map((s, i) => (
                <tr key={s.name} className="border-b border-slate-100">
                  <td className="py-2 px-2 text-slate-400">{i + 1}</td>
                  <td className="py-2 px-2 font-medium text-slate-800">{s.name}</td>
                  <td className="py-2 px-2 text-right">{s.orders}</td>
                  <td className="py-2 px-2 text-right text-slate-500">{money(s.avg_ticket)}</td>
                  <td className="py-2 px-2 text-right font-semibold">{money(s.total_spent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const COLORS = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  blue: "bg-blue-50 border-blue-200 text-blue-700",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  red: "bg-red-50 border-red-200 text-red-700",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
};

function KPI({ icon: Icon, label, value, color }) {
  const cls = COLORS[color] || COLORS.slate;
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Card({ title, subtitle, children, accent = "" }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-4 ${accent}`}>
      <div className="mb-3">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}
